import React, { FC } from 'react';
import * as Yup from 'yup';

import { Node } from 'src/common/interfaces/node.interface';
import { Step } from 'src/common/interfaces/step.interface';
import { JourneyForm, JourneyFormik } from 'src/common/JourneyFormik';
import { FormikTextInput } from 'src/common/form/formik/text-input';
import { Popover } from 'src/common/ui/popover';
import { apiAddNodeToSynced } from 'src/utils/journeyApi';
import { useEditorContext } from 'src/store/editor';
import { JourneyMenuPropsInterface } from 'src/common/JourneyMenu';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { SmallPanel } from 'src/common/ui/small-panel';

import { LeftStrongIcon } from 'src/monet/icons';

interface SyncedStepCreatePanelProps
  extends Pick<
    JourneyMenuPropsInterface,
    'renderOrigin' | 'openRef' | 'closeRef' | 'onClose' | 'onOpen' | 'offset' | 'placement'
  > {
  node: Node;
  step: Step;
  onBack?: () => void;
  open?: boolean;
}

export const SyncedStepCreatePanel: FC<SyncedStepCreatePanelProps> = ({
  node,
  step,
  renderOrigin,
  openRef,
  closeRef,
  onOpen,
  offset,
  placement,
  onClose,
  onBack,
}) => {
  const { updateNodeInJourney } = useEditorContext();
  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();

  const formikParams = {
    initialValues: {
      name: node.name,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: Yup.object().shape({
      name: Yup.string().required('Name is required'),
    }),
    onSubmit: (values: any) => {
      return apiAddNodeToSynced(node.id, values.name)
        .then((nodeResponse) => {
          updateNodeInJourney(nodeResponse, step);
          onClose && onClose();
          setSuccessNotification('Synced step successfully created.');
        })
        .catch(() => {
          setErrorNotification('Issue with creating a synced step. Please contact support.');
        });
    },
  };

  return (
    <SmallPanel
      placement={placement}
      offset={offset}
      openRef={openRef}
      openAfterMount
      fixed
      hasBackButton
      closeRef={closeRef}
      onBack={onBack}
      onOpen={onOpen}
      onClose={onClose}
      renderOrigin={renderOrigin}
    >
      <div className='flex flex-col'>
        <div className='text-bedrock-p text-bedrock-black'>
          Syncing this Step allows you to reuse it across your Journeys created - Edit one Synced Step and you edit them
          all.
        </div>
        <div className='mt-md'>
          <JourneyFormik
            {...formikParams}
            render={(formProps: any) => (
              <JourneyForm>
                <FormikTextInput
                  name='name'
                  type='text'
                  placeholder='Name this Step'
                  autoFocus={true}
                  required={true}
                  id='sync-step-name'
                />
                <button
                  type='submit'
                  className='mt-md btn-bedrock-primary'
                  disabled={formProps.values.name.length === 0}
                >
                  Create Synced Step
                </button>
              </JourneyForm>
            )}
          />
        </div>
      </div>
    </SmallPanel>
  );
};
